import React, { useEffect, useRef, useState } from "react";
import {
  createChart,
  ChartOptions,
  DeepPartial,
  ISeriesApi,
  SeriesOptionsMap,
} from "lightweight-charts";
import api from "src/store/features/api";
import { Button, Grid, Typography } from "@mui/material";
import { findNearestCandle } from "./CrosshairMoveHandler";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import ChartMetaData from "./ChartMetaData";
import { getChartOptions } from "./ChartOptions";
import GraphLoading from "./GraphLoading";
import LineChartIcon from "src/icons/LineChartIcon";
import CandleStickIcon from "src/icons/CandleStickIcon";

const buttonStyle = {
  zIndex: 10,
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px" /* 121.429% */,
  minWidth: "32x",
};

export default function TradingView(props: any) {
  const [intervalValue, setIntervalValue] = useState("1W");
  const isMobile = useIsMobile();
  const [isArea, setIsArea] = useState(false);
  const [candlestickData, setCandleStickData] = useState({
    close: 0,
    high: 0,
    low: 0,
    open: 0,
    volume: 0,
  });
  let candleSeries: ISeriesApi<keyof SeriesOptionsMap>;
  let volumeSeries: ISeriesApi<keyof SeriesOptionsMap>;
  let {
    data: candleData,
    isSuccess,
    isFetching,
  } = api.useGetCandleChartDataQuery(
    {
      token: props.currencyConfig?.code,
      interval: intervalValue,
    },
    {
      pollingInterval: 60000,
    },
  );
  const options = ["1D", "1W", "1M", "3M", "6M", "1Y"];
  const chartContainerRef = useRef<HTMLDivElement>(null); // Explicitly define the type of chartContainerRef
  const handleClick = (el: any) => {
    setIntervalValue(el);
  };

  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Update the viewport size when the component mounts and whenever the window is resized
  useEffect(() => {
    const handleResize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Attach the event listener
    window.addEventListener("resize", handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (candleData && candleData.length > 0 && isSuccess) {
      let volumeData = [];
      const areaData = [];
      for (let index = 0; index < candleData.length; index++) {
        const item = candleData[index] as any;
        const currentValue = item.volume ?? 0;
        const color = item.close < item.open ? "rgba(235,173,169,0.7)" : "rgba(160,209,204,0.7)";
        volumeData.push({
          time: item.time,
          value: currentValue,
          color: color,
        });

        areaData.push({
          time: item.time,
          value: item.close,
        });
      }

      const chartOptions: DeepPartial<ChartOptions> = getChartOptions({
        chartWidth: Math.max(viewportSize.width - 400, 250),
        viewportSize,
        isFetching,
        intervalValue,
      });

      if (!chartContainerRef.current) {
        return;
      } else {
        // create tranding view chart
        const chart = createChart(chartContainerRef.current, chartOptions); // Access current property directly

        // candlestick add to graph, if already exists, remove it.
        if (candleSeries || volumeSeries) {
          // removing
          chart.removeSeries(candleSeries);
          chart.removeSeries(volumeSeries);
        }
        if (!isArea) {
          candleSeries = chart.addCandlestickSeries();
          candleSeries.setData(JSON.parse(JSON.stringify(candleData)) ?? []);
          // parse the api number
          const customCandleData = JSON.parse(JSON.stringify(candleData));
          // replace any decimal points with empty string
          const numberString = customCandleData[0].high.toString().replace(".", "");
          // get the zeroes from string using regex
          const leadingZerosMatch = /^0*/.exec(numberString);
          // get the zero count
          const leadingZerosCount = leadingZerosMatch ? leadingZerosMatch[0].length : 0;
          // get the precision
          const precision = leadingZerosCount === 0 ? 1 : Math.pow(10, leadingZerosCount + 2);
          // convert the precision to a minimum move so 5 becomes 0.00005
          const minMove = leadingZerosCount > 0 ? 1 / precision : 0.01 / precision;

          candleSeries.applyOptions({
            priceFormat: {
              type: "price",
              precision: leadingZerosCount + 2,
              minMove: minMove,
            },
          });
        } else {
          const areaSeries = chart.addAreaSeries({
            lineColor: "#2962FF",
            topColor: "#2962FF",
            bottomColor: "rgba(41, 98, 225, 0.28)",
          });

          areaSeries.setData(areaData);
        }
        // histogram add to graph
        volumeSeries = chart.addHistogramSeries({
          priceFormat: {
            type: "volume",
          },
          priceScaleId: "left", // set as an overlay by setting a blank priceScaleId
        });
        // feed data into chart
        volumeSeries.setData(JSON.parse(JSON.stringify(volumeData)));
        // scale of histogram
        volumeSeries.priceScale().applyOptions({
          scaleMargins: {
            top: 0.7, // highest point of the series will be 70% away from the top
            bottom: 0,
          },
        });
        let minBarValue;
        let logicalRange = { from: 0, to: 10 };
        if (isMobile) {
          switch (intervalValue) {
            case "1D":
              minBarValue = 5;
              logicalRange.to = 92;
              break;
            case "1W":
              minBarValue = 0.1;
              logicalRange.to = 675;
              break;
            case "1M":
              minBarValue = 5;
              logicalRange.to = 145;
              break;
            case "3M":
              minBarValue = 10;
              logicalRange.to = 100;
              break;
            case "6M":
              minBarValue = 0.001;
              logicalRange.to = 700;
              break;
            case "1Y":
              logicalRange.to = 1580;
              minBarValue = 128;
              break;
            default:
              minBarValue = 12;
          }
        } else {
          switch (intervalValue) {
            case "1D":
              minBarValue = 5;
              logicalRange.to = 36;
              break;
            case "1W":
              minBarValue = 0.1;
              logicalRange.to = 270;
              break;
            case "1M":
              minBarValue = 5;
              logicalRange.to = 45;
              break;
            case "3M":
              minBarValue = 5;
              logicalRange.to = 140;
              break;
            case "6M":
              minBarValue = 0.001;
              logicalRange.to = 280;
              break;
            case "1Y":
              logicalRange.to = 580;
              minBarValue = 128;
              break;
            default:
              minBarValue = 12;
          }
        }
        chart.timeScale().fitContent();

        if (intervalValue === "1Y") {
          chart.timeScale().applyOptions({ fixLeftEdge: true });
          chart
            .timeScale()
            .setVisibleLogicalRange({ from: logicalRange.from, to: logicalRange.to }); // 1Y
        } else {
          chart.timeScale().applyOptions({ fixLeftEdge: true, minBarSpacing: minBarValue });
          chart
            .timeScale()
            .setVisibleLogicalRange({ from: logicalRange.from, to: logicalRange.to }); // 1Y
        }

        // Subscribe to crosshair move events
        chart.subscribeCrosshairMove((param) => {
          if (param.time) {
            const timestamp = param.time;
            // Get the candlestick data array from the series
            const data = candleData || [];
            // Find the nearest index to the crosshair's timestamp
            const index = findNearestCandle(data, +timestamp);
            if (index !== null) {
              const nearestCandle = data[index];
              const { open, high, low, close, volume } = nearestCandle;
              setCandleStickData({
                open,
                high,
                low,
                close,
                volume: volume ?? 0,
              });
            }
          }
        });
        return () => {
          chart.remove();
        };
      }
    }
  }, [isSuccess, intervalValue, isArea, isFetching, props.currencyConfig]);

  return (
    <div>
      <Grid item xs={12} sm={12} style={{ position: "relative" }}>
        <Grid
          container
          id="container"
          ref={chartContainerRef}
          height={
            viewportSize.width < 400 // mobile
              ? "300px"
              : viewportSize.width < 500 // tablet
              ? "400px"
              : viewportSize.width < 600 // desktop
              ? "500px"
              : "600px"
          }
          width={viewportSize.width < 400 ? "360px" : "auto"}
          padding={0}>
          <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-start" }}>
            {isSuccess && viewportSize.width > 400 && (
              <ChartMetaData
                currencyConfig={props.currencyConfig}
                intervalValue={intervalValue}
                candlestickData={candlestickData}
                isMobile={isMobile}
              />
            )}
            {isFetching ? (
              <div
                style={{
                  position: "absolute",
                  width: "102%", // Set width to 100% of the parent
                  height: "110%", // Set height to 100% of the parent
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 9999,
                }}>
                <GraphLoading viewportSize={viewportSize} />
              </div>
            ) : (
              (!candleData || candleData.length === 0) && (
                <div
                  style={{
                    position: "absolute",
                    width: "102%", // Set width to 100% of the parent
                    height: "110%", // Set height to 100% of the parent
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 9999,
                  }}>
                  <Typography sx={{ fontWeight: 900 }}>Data not available</Typography>
                </div>
              )
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid style={{ marginTop: viewportSize.width < 400 ? "10px" : "25px" }}>
        {
          <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <div
              style={{
                backgroundColor: "#FFF6F6",
                borderRadius: "4px",
                padding: "1px",
                marginRight: viewportSize.width < 400 ? "2px" : "8px",
              }}>
              <div style={{ backgroundColor: "#FFF", borderRadius: "5px" }}>
                <Button
                  disableRipple
                  style={{
                    ...buttonStyle,
                    backgroundColor: isArea ? "#FFF6F6" : "#FFF",
                    minWidth: "36px",
                    height: "27px",
                    borderRadius: isArea ? "6px" : "0px",
                  }}
                  onClick={() => {
                    setIsArea(!isArea);
                  }}
                  disabled={isFetching}>
                  <LineChartIcon />
                </Button>
                <Button
                  disableRipple
                  style={{
                    ...buttonStyle,
                    backgroundColor: !isArea ? "#FFF6F6" : "#FFF",
                    minWidth: "36px",
                    height: "27px",
                    borderRadius: !isArea ? "6px" : "0px",
                  }}
                  onClick={() => {
                    setIsArea(!isArea);
                  }}
                  disabled={isFetching}>
                  <CandleStickIcon />
                </Button>
              </div>
            </div>

            <div style={{ flex: "1", textAlign: viewportSize.width < 400 ? "right" : "left" }}>
              {options.map((el, it) => (
                <Button
                  disableRipple
                  key={it}
                  onClick={() => handleClick(el)}
                  sx={{
                    ...buttonStyle,
                    backgroundColor: intervalValue === el ? "#FC4C4C !important" : "white",
                    color: intervalValue === el ? "white" : "black",
                    borderRadius: "10px",
                    minWidth: viewportSize.width < 400 ? "20px" : "34px",
                    fontSize: viewportSize.width < 400 ? "12px" : "14px",
                    marginLeft: viewportSize.width < 400 ? "1px" : "4px",
                  }}
                  disabled={isFetching}>
                  {el}
                </Button>
              ))}
            </div>
          </div>
        }
      </Grid>
    </div>
  );
}
